// ConversationHistory - Chat conversation history sidebar
import React, { useState, useEffect } from 'react';
import { 
  ClockIcon, 
  TrashIcon, 
  ChatBubbleLeftIcon,
  XMarkIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';
import { aiApi } from '../../services/api';
import toast from 'react-hot-toast';

export interface Conversation {
  id: string;
  title: string;
  opportunityId?: string;
  context?: {
    opportunity?: {
      title?: string;
      agency?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  _count?: {
    messages: number;
  };
}

interface ConversationHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (conversation: Conversation) => void;
  currentConversationId?: string | null;
  opportunityId?: string;
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentConversationId,
  opportunityId,
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await aiApi.getConversations();
      setConversations(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load conversation history');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    
    if (!window.confirm('Delete this conversation? This cannot be undone.')) {
      return;
    }

    setDeletingId(conversationId);
    try {
      await aiApi.deleteConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Filter conversations for this opportunity if opportunityId is provided
  const filteredConversations = opportunityId
    ? conversations.filter(c => c.opportunityId === opportunityId)
    : conversations;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed lg:static inset-y-0 right-0 w-80 bg-white border-l border-gray-200 
          transform transition-transform duration-300 ease-in-out z-50
          flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0 lg:w-0 lg:opacity-0 lg:overflow-hidden'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">History</h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={fetchConversations}
              disabled={loading}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
              title="Refresh"
            >
              <svg 
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded lg:hidden"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {loading && conversations.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <ChatBubbleLeftIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {opportunityId 
                  ? 'No conversations for this opportunity yet.' 
                  : 'No conversations yet. Start chatting!'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredConversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => onSelect(conversation)}
                  className={`
                    w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors group
                    ${currentConversationId === conversation.id ? 'bg-brand-50 border-l-2 border-l-brand-600' : ''}
                  `}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {conversation.title || 'Untitled Conversation'}
                      </p>
                      {conversation.context?.opportunity?.title && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {conversation.context.opportunity.title}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">
                          {formatDate(conversation.updatedAt)}
                        </span>
                        {conversation._count?.messages && (
                          <span className="text-xs text-gray-400">
                            • {conversation._count.messages} messages
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleDelete(e, conversation.id)}
                      disabled={deletingId === conversation.id}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete"
                    >
                      {deletingId === conversation.id ? (
                        <div className="animate-spin h-4 w-4 border border-gray-400 border-t-transparent rounded-full" />
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 text-center">
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''} total
        </div>
      </div>

      {/* Toggle Button (visible when sidebar is closed) */}
      {!isOpen && (
        <button
          onClick={() => onClose()}
          className="fixed right-0 top-1/2 -translate-y-1/2 bg-white border border-gray-200 border-r-0 rounded-l-lg p-2 shadow-lg z-30 hover:bg-gray-50 lg:hidden"
          title="Show conversation history"
        >
          <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
        </button>
      )}
    </>
  );
};

export default ConversationHistory;
